//@flow
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from '@material-ui/core/styles';
import {usePage} from '../../../page/utils';
import {Grid, Card} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import Skeleton from '@material-ui/lab/Skeleton';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import CouponCard from './CouponCard/CouponCard';
import {useHistory, Switch, Route} from 'react-router-dom';
import CouponContainer from '../containers/CouponContainer';
import {useIsFeatureInTier} from '../../../../utils/component';

import {colors} from '../../../../assets/styles/colors';

import type {
  Currency,
  Coupon as CouponEntity,
  CouponState,
  Store,
  StoreInformation,
} from '../../../../entities';
import TierRestrictionModal from '../../../../elements/TierRestrictionModal/TierRestrictionModal';
import analytics from '../../../../analytics';
import CouponsDisabledModal from './CouponsDisabledModal/CouponsDisabledModal';
import {useMinimalTierContext} from '../../../../context/MinimalProvider';
import {useIsMinimalPlan} from '../../../store/utils';

import type {CouponRequest} from '../api';

type Props = {|
  coupons?: CouponEntity[],
  isFetchingCoupons?: boolean,
  fetchCoupons: () => void,
  error?: string,
  currency?: Currency,
  toggleFilter: (state?: CouponState) => void,
  selectedState: CouponState,
  store: Store,
  createCouponSuccess?: boolean,
  updateCouponSuccess?: boolean,
  updateCoupon?: ({couponRequest: CouponRequest, couponId: string}) => void,
  cleanCoupon: () => void,
  information?: StoreInformation,
|};

const useStyles = makeStyles(theme => ({
  root: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '16px',
    [theme.breakpoints.down('sm')]: {
      background: 'transparent',
    },
  },
  filterWrapper: {
    marginBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  bodyContent: {
    [theme.breakpoints.down('sm')]: {
      background: '#FFFFFF',
      padding: '16px',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '12px',
    },
  },
  filterButton: {
    color: colors.grey600,
    fontWeight: 'normal',
    borderRadius: '8px',
    padding: '4px 16px',
    marginRight: theme.spacing(1),
  },
  filterButtonActive: {
    color: colors.purple3,
    backgroundColor: colors.lightPurple,
    fontWeight: 600,
  },
  skeleton: {
    borderRadius: '8px',
  },
  whiteButton: {
    backgroundColor: 'white',
    color: colors.purple3,
    fontWeight: 600,
    padding: '4px 13px',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    '&:hover': {
      backgroundColor: colors.grey200,
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
    },
    marginLeft: 4,
    marginBottom: 12,
  },
}));

export default usePage<Props>(Coupons, 'Coupons');

function Coupons(props: Props) {
  const {
    information,
    isFetchingCoupons,
    coupons,
    fetchCoupons,
    error,
    currency,
    toggleFilter,
    selectedState,
    store,
    createCouponSuccess,
    updateCouponSuccess,
    cleanCoupon,
    updateCoupon,
  } = props;

  const classes = useStyles();
  const history = useHistory();

  const {
    openRestriction,
    handleCloseRestriction,
    handleOpenRestriction,
  } = useMinimalTierContext();

  const isMinimal = useIsMinimalPlan();

  const [openModalProTier, setOpenModalProTier] = useState(false);
  const handleEnabledOption = (state?: CouponState) => {
    toggleFilter(state);
  };

  const hasCouponFeature = useIsFeatureInTier('STORE_COUPON');

  const shouldRestrictedTier = !isMinimal && !hasCouponFeature;

  useEffect(() => {
    if (hasCouponFeature) fetchCoupons();
  }, [fetchCoupons, hasCouponFeature, selectedState]);

  const handleCreateCoupon = () => {
    if (!hasCouponFeature) {
      if (isMinimal) handleOpenRestriction();
      else setOpenModalProTier(true);
    } else {
      cleanCoupon();
      history.push(`/marketing/coupons/new`);
    }
  };

  useEffect(() => {
    if (createCouponSuccess) {
      analytics.fireCustomEvent('Coupon Created', undefined, ['user-guiding']);
    }
  }, [createCouponSuccess]);

  useEffect(() => {
    handleOpenRestriction();
  }, [handleOpenRestriction]);

  const filterWrapper = (
    <>
      {!shouldRestrictedTier && (
        <div className={classes.filterWrapper}>
          <CustomButton
            className={`${classes.filterButton} ${
              selectedState === '1' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption('1')}
          >
            Activos
          </CustomButton>
          <CustomButton
            className={`${classes.filterButton} ${
              selectedState === '0' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption('0')}
          >
            Inactivos
          </CustomButton>
          <CustomButton
            className={`${classes.filterButton} ${
              selectedState === '' ? classes.filterButtonActive : ''
            }`}
            onClick={() => handleEnabledOption()}
          >
            Ver Todos
          </CustomButton>
        </div>
      )}
    </>
  );
  const hasFeaturedCoupon = useMemo(() => {
    if (coupons) {
      return coupons.filter(c => c.featured).length > 0;
    } else return false;
  }, [coupons]);

  return (
    <Switch>
      <Route exact path="/marketing/coupons">
        <CouponsDisabledModal
          open={openRestriction}
          handleClose={handleCloseRestriction}
        />
        <TierRestrictionModal
          open={openModalProTier}
          handleClose={() => setOpenModalProTier(false)}
        />
        {createCouponSuccess && (
          <SnackBar
            mode="success"
            className={classes.snackBarError}
            message="Cupón creado correctamente"
          />
        )}
        {updateCouponSuccess && (
          <SnackBar
            mode="success"
            className={classes.snackBarError}
            message="Cupón actualizado correctamente"
          />
        )}
        {hasCouponFeature && <SnackBar mode="error" message={error} />}
        <Card className={classes.root}>
          <div className={classes.bodyContent}>
            <Grid container justify="space-between" direccion="row">
              {filterWrapper}
              {shouldRestrictedTier && (
                <div>
                  <CustomButton
                    className={classes.whiteButton}
                    to="/tiers-list"
                  >
                    Mejorar a TiendaDa PRO
                  </CustomButton>
                </div>
              )}
            </Grid>
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="stretch"
              spacing={3}
              style={{
                opacity: isMinimal ? 0.5 : undefined,
                pointerEvents: isMinimal ? 'none' : undefined,
              }}
            >
              {hasCouponFeature && (
                <Grid item xs={12} sm={12} md={6} lg={4} xl={3}>
                  <CouponCard type="CREATE" handleClick={handleCreateCoupon} />
                </Grid>
              )}
              {isFetchingCoupons && !coupons && !currency
                ? Array.from({length: 2}, (x, i) => (
                    <Grid item xs={12} sm={12} md={6} lg={4} xl={3} key={i}>
                      <Skeleton
                        className={classes.skeleton}
                        variant="rect"
                        height={216}
                      />
                    </Grid>
                  ))
                : coupons && coupons.length > 0
                ? coupons.map((coupon: CouponEntity, index: number) => (
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={3}
                      key={`coupon-${index}`}
                    >
                      <CouponCard
                        coupon={coupon}
                        currency={currency}
                        cleanCoupon={cleanCoupon}
                        customColor={
                          information &&
                          information.colorSetting &&
                          information.colorSetting.primaryBackgroundColor
                            ? information.colorSetting.primaryBackgroundColor
                            : '#7B39FF'
                        }
                        updateCoupon={updateCoupon}
                        store={store}
                        hasFeaturedCoupon={hasFeaturedCoupon}
                      />
                    </Grid>
                  ))
                : null}
            </Grid>
          </div>
        </Card>
      </Route>
      <Route path="/marketing/coupons/:couponId" component={CouponContainer} />
    </Switch>
  );
}

Coupons.propTypes = {
  coupons: PropTypes.array,
  isFetchingCoupons: PropTypes.bool,
  fetchCoupons: PropTypes.func.isRequired,
  error: PropTypes.string,
  currency: PropTypes.object,
  selectedState: PropTypes.string,
  toggleFilter: PropTypes.func.isRequired,
  store: PropTypes.object.isRequired,
  createCouponSuccess: PropTypes.bool,
  updateCouponSuccess: PropTypes.bool,
  cleanCoupon: PropTypes.func,
  information: PropTypes.object,
  updateCoupon: PropTypes.func,
};
